import { daysSince } from "./stages";

/* Small read-only derivations both portals need: what last happened on a case, how a
   projected fee should be captioned, and whether a case has stopped moving. Nothing here
   decides anything — the server owns stage, money and masking. */

export const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** The one line that answers "what happened last?" on a row. */
export function movement(lead) {
  const routes = lead?.routes || [];
  const stage = lead?.stage || "new";
  const declined = routes.filter((r) => r.status === "declined").length;
  const what = {
    new: "You submitted it",
    sent: `Sent to ${routes.length || "the"} desk${routes.length === 1 ? "" : "s"}`,
    in_review: "Under assessment",
    offer_made: "Offer issued",
    completed: "Funds released",
    declined: declined ? "Final desk passed" : "Closed",
  }[stage];
  return { at: lead?.updated_at || lead?.created_at, what };
}

/** How a projected fee should read under the figure. */
export function feeCaption(p) {
  if (!p) return null;
  if (p.state === "funded") return "Awaiting payout";
  if (p.state === "offer") return "On completion";
  if (p.state === "passed") return p.likely_gbp > 0 ? "Already earned" : "Closed";
  if (p.counted) return "If it completes";
  return "Not counted yet";
}

export const feeTone = (p) => {
  if (!p) return "muted";
  if (p.state === "funded") return "green";
  return p.counted ? "ink" : "muted";
};

/** Has a live case stopped moving? Days of silence, or null when it is still fresh. */
export function stalled(lead, days = 7) {
  const open = ["sent", "in_review", "offer_made"].includes(lead?.stage);
  if (!open) return null;
  const idle = daysSince(lead?.updated_at);
  return idle != null && idle >= days ? idle : null;
}

/** The edge on a table row: soft blue wants attention, amber is waiting on somebody. Declined stays calm. */
export function rowFlag(lead) {
  if (lead?.stage === "declined") return "";
  if (stalled(lead)) return "flag-amber";
  if (lead?.docs_open > 0) return "flag-amber";
  return "";
}

/** A desk's own clock: when its decision was due, and how late it is. */
export function decisionDue(row, turnaroundDays = 3) {
  const sent = row?.sent_at ? new Date(row.sent_at).getTime() : null;
  if (!sent) return null;
  const due = sent + Math.max(1, Number(turnaroundDays) || 3) * 86400000;
  const diff = due - Date.now();
  const hours = Math.round(Math.abs(diff) / 3600000);
  if (["completed", "declined"].includes(row?.status)) return { state: "closed", due };
  if (row?.status === "offer_made") return { state: "decided", due };
  if (row?.sla?.state === "waiting_client") {
    return { state: "paused", due: null, resumes: row.sla.resumes_at,
             label: row?.step === "valuation" ? "Waiting on the valuer or solicitors" : "Waiting on the client" };
  }
  if (["chasing", "overdue"].includes(row?.sla?.state)) {
    const h = row.sla.auto_pull && row.sla.hours_to_pull > 0 ? Math.max(1, Math.round(row.sla.hours_to_pull)) : null;
    return { state: "late", due: new Date(row.sla.chase_at).getTime(), label: "Decision due now", note: h ? `May move in ${h}h` : null };
  }
  if (diff < 0) {
    return { state: "late", due, label: "Decision due now", note: hours < 48 ? `${hours}h past` : `${Math.round(hours / 24)} days past` };
  }
  if (hours <= 24) return { state: "today", due, label: `${hours} hours left` };
  return { state: "ok", due, label: `${Math.round(hours / 24)} days left` };
}

export const inThisMonth = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getUTCFullYear() === n.getUTCFullYear() && d.getUTCMonth() === n.getUTCMonth();
};
